<script lang="ts" setup>
import {
    nextTick,
    onMounted,
    ref,
    toRaw,
    useSlots,
    useTemplateRef,
    watch,
} from 'vue';
import ArrowUp from '@assets/icons/arrow-up.svg';
import SearchIcon from '@assets/icons/search.svg';
import { createFetch } from '@vueuse/core';
import { addProtocol } from 'maplibre-gl';
import { PMTiles, Protocol } from 'pmtiles';
import Button from '@components/Button.vue';
import JobSearch from '@components/JobSearch.vue';
import type { JobOffering, Locality } from '@/types';

import 'maplibre-gl/dist/maplibre-gl.css';

import useJobContext from '@scripts/composables/useJobContext';
import { useJobSearchStore } from '@scripts/stores/jobSearchStore';
import { storeToRefs } from 'pinia';
import JobForm from '@components/JobForm.vue';

const props = defineProps<{
    localities: Record<string, Locality>;
    locationTypes: Record<string, string>;
}>();

const [injectContext, provideContext] = useJobContext();
provideContext(props.localities, props.locationTypes);

const container = useTemplateRef('container');
const list = useTemplateRef('list');
const map = ref<JobOfferingsMap>();
const jobOfferings = ref<JobOffering[]>([]);
const highlightedLocation = ref<number | undefined>();
const isLoading = ref(true);
const searchRadius = ref(50);
const showRadius = ref(false);
const jobSearchStore = useJobSearchStore();
const { mapBounds } = storeToRefs(jobSearchStore);

const slots = useSlots();
const hasSlot = (name) => {
    return !!slots[name];
};

const useFetch = createFetch({
    baseUrl: '/api/',
    options: {
        beforeFetch() {
            isLoading.value = true;
        },
        afterFetch({ data, response }) {
            isLoading.value = false;

            const json = JSON.parse(data);
            map.value?.updateData(json);

            return { data, response };
        },
    },
});

const protocol = new Protocol();
addProtocol('pmtiles', protocol.tile);

const tiles = new PMTiles(window.location.origin + '/map.pmtiles');
protocol.add(tiles);

function requestGeolocation(): Promise<boolean> {
    if (!map.value) return new Promise((resolve) => resolve(false));

    return map.value.geolocate();
}

function onUpdateLocation(location?: string) {
    if (!location) {
        showRadius.value = false;
        map.value?.setCenter(null);
        return;
    }

    showRadius.value = true;

    if (location === '__geolocate') {
        map.value?.fitToRadius(searchRadius.value);
        return;
    }

    const coords = props.localities[location]?.coordinates;
    map.value?.setCenter(coords as [number, number]);
    map.value?.fitToRadius(searchRadius.value, coords);
}

function queryTags() {
    useFetch(
        'job-offerings.geojson?' +
            jobSearchStore.buildParams(false, map.value?.visibleFeatureIds)
    );
}

function queryTagsWithSearch() {
    useFetch(
        `job-offerings.geojson?${jobSearchStore.buildParams(true, map.value?.visibleFeatureIds)}`
    );
}

watch(searchRadius, (distance) => {
    map.value?.fitToRadius(distance);
});

onMounted(() => {
    tiles.getHeader().then((header) => {
        map.value = new JobOfferingsMap({
            geoJsonUrl: `${window.location.origin}/api/job-offerings.geojson?${jobSearchStore.buildParams(true)}`,
            container: container.value as HTMLElement,
            maxBounds: [
                [header.minLon, header.minLat],
                [header.maxLon, header.maxLat],
            ],
            currentBounds: toRaw(mapBounds.value),
            onInteractionStart: () => {
                isLoading.value = true;
            },
            onInteractionEnd: () => {
                isLoading.value = false;
                // queryTags();
            },
            onMarkerClick: (location) => {
                highlightedLocation.value = location.externalId;

                nextTick(() => {
                    list.value
                        ?.querySelector(
                            `[data-location="${location.externalId}"]`
                        )
                        ?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'start',
                            container: 'nearest',
                        });
                });
            },
            onUpdateBounds: (features) => {
                jobOfferings.value = features.reduce((acc, curr) => {
                    acc.push(...curr.properties.jobOfferings);
                    return acc;
                }, [] as JobOffering[]);
            },
        });
    });

    window.addEventListener('beforeunload', () => {
        mapBounds.value = map.value?.getBounds();
    });
});
</script>

<script lang="ts">
import { layers } from '@protomaps/basemaps';
import flavor from '@scripts/lib/map-style.js';
import bbox from '@turf/bbox';
import circle from '@turf/circle';
import type {
    Feature,
    FeatureCollection,
    GeoJsonProperties,
    Point,
} from 'geojson';
import {
    GeoJSONSource,
    GeolocateControl,
    LngLatBounds,
    Map,
    MapMouseEvent,
    NavigationControl,
    type LngLatBoundsLike,
    type LngLatLike,
} from 'maplibre-gl';

type LocationProperties = GeoJsonProperties & {
    id: number;
    externalId: number;
    title: string;
    address: string;
    typeId: number[];
    count: number;
    jobOfferings: JobOffering[];
};

class JobOfferingsMap {
    public map: Map;

    private bounds: LngLatBounds;
    private features: Feature<Point, LocationProperties>[] = [];
    private geolocation: [number, number] | null = null;
    private center: [number, number] | null = null;

    private readonly geolocateControl: GeolocateControl;

    private readonly onMarkerClick: (location: LocationProperties) => void;
    private readonly onUpdateBounds: (
        visibleFeatures: Feature<Point, LocationProperties>[]
    ) => void;

    constructor({
        geoJsonUrl,
        container,
        maxBounds,
        currentBounds,
        onInteractionStart = () => {},
        onInteractionEnd = () => {},
        onMarkerClick = () => {},
        onUpdateBounds = () => {},
    }: {
        geoJsonUrl: string;
        container: HTMLElement;
        maxBounds: LngLatBoundsLike;
        currentBounds?: LngLatBoundsLike | undefined;
        onInteractionStart?: () => void;
        onInteractionEnd?: () => void;
        onMarkerClick?: (location: LocationProperties) => void;
        onUpdateBounds?: (
            visibleFeatures: Feature<Point, LocationProperties>[]
        ) => void | (() => void);
    }) {
        this.bounds = new LngLatBounds(maxBounds as [LngLatLike, LngLatLike]);
        this.onUpdateBounds = onUpdateBounds;
        this.onMarkerClick = onMarkerClick;

        this.map = new Map({
            container,
            maxBounds,
            center: this.bounds.getCenter(),
            zoom: 7,
            maxZoom: 18,
            cooperativeGestures: matchMedia('(pointer:coarse)').matches,
            locale: {
                'CooperativeGesturesHandler.MobileHelpText':
                    'Karte mit zwei Fingern verschieben',
            },
            style: {
                version: 8,
                sources: {
                    protomaps: {
                        type: 'vector',
                        url: `pmtiles://${window.location.origin}/map.pmtiles`,
                        attribution:
                            '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
                    },
                    locations: {
                        type: 'geojson',
                        data: geoJsonUrl,
                        cluster: true,
                        clusterMaxZoom: 13,
                        clusterRadius: 60,
                        clusterProperties: {
                            count: ['+', ['get', 'count']],
                            locations: [
                                'concat',
                                ['concat', ['get', 'externalId'], ','],
                            ],
                        },
                    },
                },
                layers: layers('protomaps', flavor, { lang: 'de' }),
                sprite: `${window.location.origin}/assets/sprite`,
                glyphs: `${window.location.origin}/assets/{fontstack}/{range}.pbf`,
            },
        });

        this.geolocateControl = new GeolocateControl({
            fitBoundsOptions: {
                zoom: 10,
            },
            positionOptions: {
                enableHighAccuracy: true,
            },
            trackUserLocation: true,
        });

        this.init();

        this.map.on('load', () => {
            // Set bounds after map loads to avoid race condition with PMTiles
            if (currentBounds) {
                this.map.fitBounds(currentBounds, { duration: 0 });
            }

            this.onLoad();
            onInteractionEnd();
            this.updateBounds();
        });

        this.map.on('click', this.onClick.bind(this));

        this.map.on('mouseenter', 'markers', (e) => {
            this.map.getCanvas().style.cursor = 'pointer';
        });

        this.map.on('mouseleave', 'markers', () => {
            this.map.getCanvas().style.cursor = '';
        });

        this.map.on('movestart', onInteractionStart);
        this.map.on('moveend', () => {
            this.updateBounds();
            onInteractionEnd();
        });

        this.map.on('zoomstart', onInteractionStart);
        this.map.on('zoomend', () => {
            this.updateBounds();
            onInteractionEnd();
        });
    }

    private init() {
        this.map.addControl(this.geolocateControl);

        this.map.addControl(
            new NavigationControl({
                showCompass: false,
            })
        );

        this.geolocateControl.on('geolocate', (data) => {
            if (!data.coords) {
                this.geolocation = null;
                return;
            }

            this.geolocation = [
                data.coords.longitude,
                data.coords.latitude,
            ] as [number, number];
        });

        this.geolocateControl.on('trackuserlocationend', () => {
            this.geolocation = null;
        });
    }

    private async onLoad() {
        await Promise.all([
            this.map.loadImage('/assets/marker-red.png'),
            this.map.loadImage('/assets/marker-black.png'),
            this.map.loadImage('/assets/badge-1.png'),
            this.map.loadImage('/assets/badge-2.png'),
            this.map.loadImage('/assets/badge-3.png'),
        ]).then(([markerRed, markerBlack, badge1, badge2, badge3]) => {
            this.map.addImage('marker-red', markerRed.data);
            this.map.addImage('marker-black', markerBlack.data);
            this.map.addImage('badge-1', badge1.data);
            this.map.addImage('badge-2', badge2.data);
            this.map.addImage('badge-3', badge3.data);
        });

        const source = this.map.getSource('locations') as GeoJSONSource;
        this.features = (
            (await source.getData()) as FeatureCollection<
                Point,
                LocationProperties
            >
        ).features;

        this.map.addLayer({
            id: 'jobcount',
            type: 'symbol',
            source: 'locations',
            layout: {
                'icon-image': [
                    'image',
                    [
                        'at',
                        ['-', ['length', ['to-string', ['get', 'count']]], 1],
                        ['literal', ['badge-1', 'badge-2', 'badge-3']],
                    ],
                ],
                'icon-size': 0.5,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'text-field': ['get', 'count'],
                'text-font': ['SourceSans3-Bold'],
                'text-size': 18,
            },
            paint: {
                'text-color': '#dd0000',
                'icon-translate': [20, -64],
                'text-translate': [20, -64],
            },
        });

        this.map.addLayer({
            id: 'markers',
            type: 'symbol',
            source: 'locations',
            layout: {
                'icon-image': [
                    'image',
                    [
                        'case',
                        [
                            'case',
                            ['has', 'id'],
                            // actual markers, which have an id and the global state equals their id
                            [
                                '==',
                                ['global-state', 'highlight'],
                                ['get', 'externalId'],
                            ],
                            ['has', 'point_count'],
                            // clusters, which contain the global state location id
                            [
                                'in',
                                ['global-state', 'highlight'],
                                ['get', 'locations'],
                            ],
                            false,
                        ],
                        'marker-black',
                        'marker-red',
                    ],
                ],
                'icon-size': 0.5,
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
            },
        });

        this.onUpdateBounds(this.visibleFeatures);
    }

    private async onClick(e: MapMouseEvent) {
        const features = this.map.queryRenderedFeatures(e.point, {
            layers: ['markers'],
        });

        if (!features.length) {
            this.resetHighlightedMarker();
            return;
        }

        if (!features[0]) return;

        const { properties, geometry } = features[0] as any;

        if (properties.cluster) {
            const clusterId = properties.cluster_id;
            const source = this.map.getSource('locations') as GeoJSONSource;
            const zoom = await source.getClusterExpansionZoom(clusterId);

            this.map.flyTo({
                center: geometry.coordinates,
                zoom,
                essential: true,
            });

            return;
        }

        this.setHighlightedMarker(
            properties.externalId as LocationProperties['externalId']
        );
        this.onMarkerClick(properties as LocationProperties);
    }

    private updateBounds() {
        this.bounds = this.map.getBounds();
        this.onUpdateBounds(this.visibleFeatures);
    }

    public getBounds(): LngLatBoundsLike {
        return this.map.getBounds().toArray() as LngLatBoundsLike;
    }

    public setHighlightedMarker(locationId: number) {
        this.map.setGlobalStateProperty('highlight', locationId);
    }

    public resetHighlightedMarker() {
        this.map.setGlobalStateProperty('highlight', null);
    }

    public setCenter(center: [number, number] | null) {
        this.center = center;
    }

    public geolocate(): Promise<boolean> {
        return new Promise((resolve) => {
            this.geolocateControl.once('geolocate', (data) => {
                if (!data.coords) {
                    resolve(false);
                }

                resolve(true);
            });
            this.geolocateControl.once('error', (e) => {
                console.warn(e);
                resolve(false);
            });
            this.geolocateControl.trigger();
        });
    }

    public fitToRadius(radius: number, center: [number, number] | null = null) {
        if (!center && this.center !== null) {
            center = this.center;
        }

        if (!center && this.geolocation !== null) {
            center = this.geolocation;
        }

        const rangeCircle = circle(
            center ?? this.map.getCenter().toArray(),
            radius,
            { units: 'kilometers' }
        );

        const bounds = bbox(rangeCircle) as LngLatBoundsLike;

        this.map.fitBounds(bounds);
    }

    public updateData(geojson: FeatureCollection<Point, LocationProperties>) {
        const source = this.map.getSource('locations') as GeoJSONSource;
        source.setData(geojson);
        this.features = geojson.features;
        this.onUpdateBounds(this.visibleFeatures);
    }

    get visibleFeatures() {
        return this.features.filter((feature) => {
            return this.bounds.contains(
                feature.geometry.coordinates as LngLatLike
            );
        });
    }

    get visibleFeatureIds() {
        return this.visibleFeatures.map((feature) => feature.properties.id);
    }
}
</script>

<template>
    <div class="space-y-16">
        <div class="lg:container-content-sm">
            <div class="flex justify-center space-x-[-2px]">
                <div
                    class="border-primary text-primary hidden items-center justify-center rounded-l-full border-2 p-5 pl-7.5 sm:flex"
                >
                    <SearchIcon class="size-7.5" />
                </div>

                <JobSearch
                    class="flex-1"
                    :on-request-geolocation="requestGeolocation"
                    @search="queryTagsWithSearch"
                    @update-tag="queryTags"
                    @update-location="onUpdateLocation"
                />

                <div
                    v-if="showRadius"
                    class="border-primary flex border-y-2 px-5"
                >
                    <select
                        class="text-base font-bold focus:outline-0"
                        v-model="searchRadius"
                    >
                        <option value="10">bis 10&thinsp;km</option>
                        <option value="25">bis 25&thinsp;km</option>
                        <option
                            value="50"
                            selected
                        >
                            bis 50&thinsp;km
                        </option>
                        <option value="100">bis 100&thinsp;km</option>
                    </select>
                </div>

                <Button
                    variant="outline"
                    class="h-auto flex-none rounded-l-none pr-7.5"
                    @click="queryTagsWithSearch"
                >
                    Suchen
                </Button>
            </div>
        </div>

        <div
            class="md:container-content-sm lg:container-content xl:container-inner flex flex-col overflow-hidden rounded-b md:px-0 lg:h-[80vh] lg:flex-row-reverse"
        >
            <div
                ref="container"
                class="relative h-[65vh] lg:h-full lg:w-[60%]"
            ></div>
            <div
                ref="list"
                class="relative h-[65vh] overflow-x-hidden overflow-y-auto overscroll-contain bg-neutral-100 px-5 lg:h-full lg:w-[40%] dark:bg-neutral-800"
            >
                <svg
                    class="fill-primary absolute top-1/2 left-1/2 size-10 -translate-x-1/2 -translate-y-full animate-spin"
                    :class="{
                        hidden: !isLoading,
                    }"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 256 256"
                >
                    <path
                        d="M232,128a104,104,0,0,1-208,0c0-41,23.81-78.36,60.66-95.27a8,8,0,0,1,6.68,14.54C60.15,61.59,40,93.27,40,128a88,88,0,0,0,176,0c0-34.73-20.15-66.41-51.34-80.73a8,8,0,0,1,6.68-14.54C208.19,49.64,232,87,232,128Z"
                    ></path>
                </svg>
                <p
                    class="text-primary sticky top-0 z-10 -mx-5 bg-neutral-100 px-5 py-5 text-xs font-bold uppercase dark:bg-neutral-800"
                >
                    {{ jobOfferings.length }} Stellenangebote gefunden
                </p>
                <div
                    v-if="jobOfferings.length === 0 && !isLoading"
                    class="text-sm/normal font-bold"
                >
                    Keine passenden Stellenangebote im Kartenbereich.
                </div>
                <div
                    class="divide-primary -mt-5 divide-y-2 text-sm leading-normal transition-opacity"
                    :class="{
                        'pointer-events-none opacity-10': isLoading,
                    }"
                >
                    <details
                        v-for="jobOffering in jobOfferings"
                        :key="jobOffering.id"
                        class="group/detail scroll-mt-12"
                        :data-location="jobOffering.organization.externalId"
                        v-bind="{
                            open: !!(
                                highlightedLocation &&
                                highlightedLocation ==
                                    jobOffering.organization.externalId
                            ),
                        }"
                        @mouseenter="
                            () => {
                                map?.setHighlightedMarker(
                                    +jobOffering.organization.externalId
                                );
                            }
                        "
                        @mouseleave="
                            () => {
                                map?.resetHighlightedMarker();
                            }
                        "
                    >
                        <summary
                            class="group/summary focusable text-primary active:text-foreground flex cursor-pointer list-none items-center justify-between gap-2.5"
                        >
                            <span class="flex max-w-full flex-col py-5">
                                <span
                                    class="font-bold hyphens-auto uppercase"
                                    >{{ jobOffering.title }}</span
                                >
                                <span>
                                    {{ jobOffering.organization.name }}
                                    <span
                                        v-if="
                                            jobOffering.applicationDeadline !==
                                            null
                                        "
                                    >
                                        &mdash; Bewerbungsschluss
                                        {{ jobOffering.applicationDeadline }}
                                    </span>
                                </span>
                            </span>

                            <span
                                class="ring-primary group-active/summary:ring-foreground rounded-full p-4 group-hover/summary:ring-2"
                            >
                                <ArrowUp
                                    class="translate size-10 rotate-90 transition duration-200 group-open/detail:rotate-0"
                                />
                            </span>
                        </summary>

                        <div class="pb-5">
                            <p>
                                Wo:
                                <strong>{{
                                    jobOffering.organization.name
                                }}</strong>
                            </p>
                            <p v-if="jobOffering.jobStartDate !== null">
                                Wann:
                                <strong>{{ jobOffering.jobStartDate }}</strong>
                            </p>
                            <p>
                                Teilzeiteignung:
                                <strong>{{
                                    jobOffering.isPartTimePossible
                                        ? 'Ja'
                                        : 'Nein'
                                }}</strong>
                            </p>
                            <p>
                                Besoldungs-/Entgeltgruppe:
                                <strong>{{ jobOffering.salaryGrade }}</strong>
                            </p>
                            <p v-if="jobOffering.applicationDeadline !== null">
                                Bewerbungsschluss:
                                <strong>{{
                                    jobOffering.applicationDeadline
                                }}</strong>
                            </p>
                        </div>

                        <div
                            class="flex flex-col gap-y-5 pb-10 *:w-full *:last:rounded-b md:*:flex-1"
                        >
                            <a
                                :href="jobOffering.url"
                                class="focusable border-primary bg-primary text-primary-foreground hover:bg-primary-foreground hover:text-primary flex cursor-pointer items-center justify-center gap-x-10 border-2 px-10 py-5 text-sm font-bold uppercase active:border-black active:bg-black active:text-white"
                            >
                                Mehr Informationen
                            </a>
                            <!--                            <a-->
                            <!--                                href="#!"-->
                            <!--                                class="border-primary text-primary hover:bg-primary hover:text-primary-foreground focusable flex cursor-pointer items-center justify-center gap-x-10 border-2 px-10 py-5 text-sm font-bold uppercase active:border-black active:bg-black active:text-white"-->
                            <!--                            >-->
                            <!--                                Jetzt online bewerben-->
                            <!--                            </a>-->
                        </div>
                    </details>
                </div>
            </div>
        </div>

        <JobForm v-if="hasSlot('form')">
            <slot name="form" />
        </JobForm>
    </div>
</template>
